import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { FieldList } from 'axway-react-ui';
import * as utils from 'components/utility/utils';

class ModelFieldList extends React.Component {
	constructor(props) {
		super(props);
		this.getModelFields = this.getModelFields.bind(this);
		this.renderFieldList = this.renderFieldList.bind(this);
		this.renderInnerModel = this.renderInnerModel.bind(this);
		this.renderNote = this.renderNote.bind(this);
		IntlComponent.init(this);
	}

	renderNote(modelName, detail) {
		const { editable, onViewModel } = this.props;
		if (!modelName && !detail) {
			return;
		}
		let modelHTML;
		if (!editable && modelName) {
			modelHTML = (
				<a
					className="note-text"
					href={`${window.$conf.prefix}/project/models/${encodeURIComponent(modelName)}`}
					onClick={e => {
						e.preventDefault();
						if (onViewModel) {
							onViewModel(modelName);
						}
					}}
				>
					{modelName}
				</a>
			);
		} else if (modelName) {
			modelHTML = <span className="note-text">{modelName}</span>;
		}

		if (!detail) {
			return modelHTML;
		}
		return (
			<div>
				{modelHTML}
				<span className="note-detail">{detail}</span>
			</div>
		);
	}

	getModelFields(model, isInner) {
		const { onEdit, onDelete } = this.props;
		const fields = [];
		Object.keys(model.fields).forEach((fieldName) => {
			const field = model.fields[fieldName];
			const fieldType = utils.objectTypeToString(field.type);
			const modelFieldName = field.model ? field.name : undefined;
			let note;
			let noteDetail;
			let hasDetails = false;
			if (isInner) {
				// if it's a field that's part of a sub-model then just show the sub-model name
				note = model.name;
			}
			if (model._joinmeta_ && field.model && !isInner) {
				const modelMergeTypes = model._joinmeta_.modelMergeTypes || {};
				if (modelMergeTypes[field.model] && modelMergeTypes[field.model] !== 'fields') {
					const parent = model._joinmeta_.parent;
					// if the field is a joined object/array
					noteDetail = model._joinmeta_.type === 'left_join'
						? this.intl.formatMsg('Left join with {parent}', { parent })
						: this.intl.formatMsg('Inner join with {parent}', { parent });
					hasDetails = true;
				} else if (modelFieldName !== fieldName) {
					noteDetail = modelFieldName;
				}
				note = this.renderNote(field.model, noteDetail);
			}

			fields.push(
				<FieldList.Field
					key={fieldName}
					name={fieldName}
					type={fieldType}
					description={field.description}
					note={note}
					hasDetails={hasDetails}
					readOnly={field.readonly}
					required={field.required}
					default={field.default}
					onEdit={!isInner ? onEdit : undefined}
					onDelete={!hasDetails && !isInner ? onDelete : undefined}
				/>
			);
		});
		return fields;
	}

	renderInnerModel(item) {
		const { model, models, editable } = this.props;
		if (!item.hasDetails || !models || !model) {
			return;
		}
		const innerModel = models[model.fields[item.name].model];
		if (!innerModel) {
			return;
		}
		return this.renderFieldList(innerModel, editable, false, true);
	}

	renderFieldList(model, editable, hasInnerList, isInnerList) {
		let innerRender;
		if (hasInnerList) {
			innerRender = this.renderInnerModel;
		} else if (isInnerList) {
			innerRender = () => false;
		}

		let hasModels = false;
		Object.keys(model.fields).forEach(name => {
			const field = model.fields[name];
			hasModels = hasModels || !!field.model;
		});

		// Only show source column if there are any to link.
		let noteColumnName;
		if (hasModels || isInnerList) {
			noteColumnName = this.intl.formatMsg('Source');
		}

		const classes = [ 'ux-container' ];
		if (isInnerList) {
			classes.push('inner-field-list');
		}

		return (
			<FieldList
				className={classes.join(' ')}
				editable={editable}
				deletable={editable}
				headers={!isInnerList}
				fieldDetailRender={innerRender}
				emptyMessage={this.intl.formatMsg('This model has no fields.')}
				noteColumnName={noteColumnName}
			>
				{this.getModelFields(model, isInnerList)}
			</FieldList>
		);
	}

	render() {
		const { editable,	model, className } = this.props;
		const classes = [ 'model-field-list' ];
		if (className) {
			classes.push(className);
		}

		const joins = model && model._joinmeta_ && model._joinmeta_.joins
			? model._joinmeta_ && model._joinmeta_.joins : [];
		let hasInnerList;
		joins.forEach(join => {
			hasInnerList = hasInnerList || join.merge !== 'fields';
		});

		return (
			<div className={classes.join(' ')}>
				{model && this.renderFieldList(model, editable, hasInnerList)}
			</div>
		);
	}
}

ModelFieldList.propTypes = {
	/**
	 * Class name to apply to component.
	 */
	className: PropTypes.string,

	/**
	 * If false, shows in read only mode
	 */
	editable: PropTypes.bool,

	/**
	 * Model to show fields for
	 */
	model: PropTypes.object.isRequired,

	/**
	 * Edit action for field
	 */
	onEdit: PropTypes.func,

	/**
	 * Delete action for field
	 */
	onDelete: PropTypes.func,

	/**
	 * Models that may be linked to main model by join
	 */
	models: PropTypes.object,

	/**
	 * Called in edit mode when clicking on the model name in the source column
	 */
	onViewModel: PropTypes.func
};

ModelFieldList.defaultProps = {
	editable: false,
	models: {}
};

export default injectIntl(ModelFieldList);
